import { createTheme } from "@material-ui/core";
import typography from "./typography";

// All accent colors resolve through CSS custom properties (--accent-*),
// which are defined in App.css and switched at runtime by the accent picker.
const customTokens = {
    accent: {
        violet: "rgb(var(--accent-1))",
        cyan: "rgb(var(--accent-2))",
        fuchsia: "rgb(var(--accent-3))",
    },
    gradient: "linear-gradient(135deg, rgb(var(--accent-1)) 0%, rgb(var(--accent-2)) 100%)",
    gradientVibrant:
        "linear-gradient(135deg, rgb(var(--accent-1-light)) 0%, rgb(var(--accent-3)) 45%, rgb(var(--accent-2)) 100%)",
    gradientSoft: "linear-gradient(135deg, rgba(var(--accent-1),0.14) 0%, rgba(var(--accent-2),0.14) 100%)",
    glass: {
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.09)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
    },
    glow: {
        violet: "0 0 42px rgba(var(--accent-1),0.35)",
        cyan: "0 0 42px rgba(var(--accent-2),0.28)",
        soft: "0 8px 32px rgba(0,0,0,0.45)",
    },
    monoFont: '"JetBrains Mono", "Fira Code", monospace',
};

// Physical 3D buttons: lit top bevel, solid lower edge for thickness, light sweep on hover, press-down on click.
const sheen = {
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.25s ease, filter 0.25s ease",
    "& .MuiButton-label": { position: "relative", zIndex: 1 },
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        bottom: 0,
        left: "-60%",
        width: "40%",
        background: "linear-gradient(105deg, transparent, rgba(255,255,255,0.4), transparent)",
        transform: "skewX(-20deg)",
        transition: "left 0.6s ease",
        pointerEvents: "none",
    },
    "&:hover::after": { left: "130%" },
    "&:hover": { transform: "translateY(-2px)" },
    "&:active": { transform: "translateY(3px)" },
};

// Thick glass slab: lit top bevel, dark lower edge and stacked depth shadows.
customTokens.surface3d = {
    // Opaque rather than backdrop-blurred: blurring over the animated WebGL background repaints every frame.
    background: "linear-gradient(180deg, rgba(26,28,36,0.92) 0%, rgba(14,15,21,0.92) 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderTopColor: "rgba(255,255,255,0.18)",
    boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.4), 0 2px 0 rgba(0,0,0,0.4), 0 6px 0 -2px rgba(0,0,0,0.3), 0 28px 50px -14px rgba(0,0,0,0.7)",
};

// Cursor-follow tilt with a moving glare; pair with the useTilt hook which sets --rx/--ry/--gx/--gy.
customTokens.tilt3d = {
    position: "relative",
    transform: "perspective(1100px) translateY(var(--lift, 0px)) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg))",
    transition: "transform 0.25s ease-out, box-shadow 0.35s ease, border-color 0.35s ease",
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 3,
        borderRadius: "inherit",
        pointerEvents: "none",
        background: "radial-gradient(circle at var(--gx, 50%) var(--gy, 0%), rgba(255,255,255,0.11), transparent 50%)",
        opacity: 0,
        transition: "opacity 0.3s ease",
    },
    "&:hover::after": { opacity: 1 },
    "@media (prefers-reduced-motion: reduce)": { transform: "none" },
};

// Raised keyboard key, presses down on hover.
customTokens.keycap = {
    background: "linear-gradient(180deg, #24262d 0%, #15161b 100%)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderTopColor: "rgba(255,255,255,0.15)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.07), 0 3px 0 #07080b, 0 3px 0 1px rgba(0,0,0,0.35), 0 8px 14px rgba(0,0,0,0.45)",
    transition: "transform 0.12s ease, box-shadow 0.12s ease, color 0.2s ease, border-color 0.2s ease",
    "&:hover": {
        transform: "translateY(2px) !important",
        color: "rgb(var(--accent-1-light))",
        borderColor: "rgba(var(--accent-1),0.5)",
        boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.07), 0 1px 0 #07080b, 0 1px 0 1px rgba(0,0,0,0.35), 0 3px 6px rgba(0,0,0,0.45), 0 0 18px rgba(var(--accent-1),0.35)",
    },
};

customTokens.button3d = {
    primary: {
        ...sheen,
        color: "#fff",
        textShadow: "0 1px 1px rgba(0,0,0,0.3)",
        background:
            "linear-gradient(180deg, rgb(var(--accent-1-light)) 0%, rgb(var(--accent-1)) 50%, rgb(var(--accent-1-dark)) 100%)",
        boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 0 rgba(0,0,0,0.2), 0 4px 0 rgb(var(--accent-1-dark)), 0 4px 0 1px rgba(0,0,0,0.35), 0 12px 28px rgba(var(--accent-1),0.35)",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 1,
            left: 1,
            right: 1,
            height: "48%",
            borderRadius: "inherit",
            background: "linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0.04))",
            pointerEvents: "none",
        },
        "&:hover": {
            ...sheen["&:hover"],
            filter: "brightness(1.06)",
            boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 0 rgba(0,0,0,0.2), 0 6px 0 rgb(var(--accent-1-dark)), 0 6px 0 1px rgba(0,0,0,0.35), 0 18px 40px rgba(var(--accent-1),0.5)",
        },
        "&:active": {
            ...sheen["&:active"],
            boxShadow:
                "inset 0 2px 4px rgba(0,0,0,0.25), 0 1px 0 rgb(var(--accent-1-dark)), 0 1px 0 1px rgba(0,0,0,0.35), 0 6px 14px rgba(var(--accent-1),0.3)",
        },
    },
    glass: {
        ...sheen,
        color: "#e8eaf2",
        background: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.03) 100%)",
        border: "1px solid rgba(255,255,255,0.14)",
        borderTopColor: "rgba(255,255,255,0.3)",
        backdropFilter: "blur(12px) saturate(140%)",
        WebkitBackdropFilter: "blur(12px) saturate(140%)",
        boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.16), 0 4px 0 rgba(0,0,0,0.5), 0 12px 28px rgba(0,0,0,0.4)",
        "&:hover": {
            ...sheen["&:hover"],
            borderColor: "rgba(var(--accent-1),0.6)",
            boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.2), 0 6px 0 rgba(0,0,0,0.5), 0 16px 36px rgba(0,0,0,0.45), 0 0 26px rgba(var(--accent-1),0.35)",
        },
        "&:active": {
            ...sheen["&:active"],
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.35), 0 1px 0 rgba(0,0,0,0.5), 0 4px 10px rgba(0,0,0,0.4)",
        },
    },
};

const baseTheme = {
    breakpoints: {
        values: {
            xs: 0,
            sm: 700,
            md: 960,
            lg: 1320,
            xl: 1920,
        },
    },
    navbarHeight: "72px",
    mobileNavbarHeight: "60px",
    typography,
    overrides: {
        MuiButton: {
            root: {
                textTransform: "none",
                fontSize: "15px",
                borderRadius: 10,
                fontWeight: 500,
            },
        },
        MuiOutlinedInput: {
            root: {
                backgroundColor: "rgba(0,0,0,0.28)",
                // Recessed into the card: dark inner shadow on top, faint highlight on the lower lip.
                boxShadow: "inset 0 2px 6px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.05)",
                borderRadius: 12,
                transition: "box-shadow 0.25s ease, border-color 0.25s ease",
                "&:hover $notchedOutline": {
                    borderColor: "rgba(var(--accent-1),0.5)",
                },
                "&$focused $notchedOutline": {
                    borderColor: "rgb(var(--accent-1))",
                    borderWidth: 1,
                    boxShadow: "0 0 24px rgba(var(--accent-1),0.25)",
                },
            },
            notchedOutline: {
                borderColor: "rgba(255,255,255,0.12)",
            },
            input: {
                color: "#e8eaf2",
            },
        },
        MuiInputLabel: {
            root: {
                color: "#9aa3b8",
                "&$focused": {
                    color: "rgb(var(--accent-1-light))",
                },
            },
        },
        MuiFormHelperText: {
            root: {
                fontFamily: customTokens.monoFont,
                fontSize: 12,
            },
        },
    },
};

const darkTheme = createTheme({
    palette: {
        type: "dark",
        background: {
            default: "#06060c",
            paper: "#0c0c16",
        },
        primary: {
            main: "rgb(var(--accent-1))",
            light: "rgb(var(--accent-1-light))",
            dark: "rgb(var(--accent-1-dark))",
            contrastText: "#fff",
        },
        secondary: {
            main: "rgb(var(--accent-2))",
            light: "rgb(var(--accent-2-light))",
            dark: "rgb(var(--accent-2-dark))",
            contrastText: "#06060c",
        },
        text: {
            primary: "#e8eaf2",
            secondary: "#9aa3b8",
        },
        action: {
            disabled: "rgba(255,255,255,0.35)",
            disabledBackground: "rgba(255,255,255,0.08)",
        },
        divider: "rgba(255,255,255,0.08)",
    },
    logoColor: "#fff",
    ...customTokens,
    ...baseTheme,
});

// The portfolio is now a polished dark-only experience. The alias is kept
// so existing imports keep working.
const lightTheme = darkTheme;

export { darkTheme, lightTheme };
